import type { Task, TaskDetail as TaskRecord } from '@dyspatch/core';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, ListTodo } from 'lucide-react';
import type { ReactNode } from 'react';
import { Empty, EventLogSection, RepoChip, SizeChip, TaskKey } from '@/components/common/bits';
import { TaskStateChip } from '@/components/common/StatusIcon';
import { Page, ViewHeader } from '@/components/layout/Page';
import { Section, SectionHead, Surface } from '@/components/ui/section';
import { PausedChip } from '@/features/actions/PauseActions';
import { TaskNotes } from '@/features/tasks/TaskNotes';
import {
  BriefLink,
  RunSummary,
  TaskAttempts,
  TaskDecision,
  TaskProgressList,
  TaskProperties,
  type TaskShape,
  TaskSpec,
  TaskVerbsPopover,
  taskDecision,
  taskProgressSteps,
  useTaskShape,
} from '@/features/tasks/TaskParts';
import { api } from '@/lib/api';
import { duration, minutesSince } from '@/lib/format';
import { hubKeys } from '@/lib/query';
import { cn } from '@/lib/utils';

/**
 * One task, as a page (issue #61).
 *
 * `/tasks/:taskId` used to render the same 400px pane the board pops out at the
 * side, so the widest thing on the page was that column, the event log was the
 * third scroller inside it, and the two facts that say what the task actually
 * is - the spec and the acceptance criteria - were drawn nowhere at all. A task
 * carries more than a job does (a spec, criteria, a repo, a size, an edge, a
 * worker, attempts, a branch, a PR, notes, findings and a journal) and it was
 * being drawn in a quarter of the room.
 *
 * The page reads:
 *
 *  - the header answers what this task is, whose job it belongs to and what
 *    state it is in, with the way back to the job beside it;
 *  - the decision, if there is one, is pinned under the header, because it is
 *    why most people opened the task;
 *  - the properties next, full width: every fact and every instant as one band
 *    of icon, label and value, the thing you glance at rather than read;
 *  - the spec under that at a reading width, with the run it produced beside
 *    it in a rail: the notes and findings, their verbs intact, and the journal.
 *
 * **There is one scroller on this page: the window.** Below `xl` the two
 * columns become one and the page reads top to bottom, which is what makes it
 * work on a phone: the decision is still first, the properties are still a
 * band, the spec is still prose, and the rail becomes the region under it.
 *
 * The board keeps its own peek pane for a glance and links here for the rest,
 * so neither surface is the other's poor copy.
 */

/** The header and an empty page under it, for loading and for a bad id. */
function Shell({ title, children }: { title: string; children: ReactNode }) {
  return (
    <>
      {/* The way out is the jobs list rather than the job: an id that did not
          resolve has no job behind it to go back to. */}
      <ViewHeader
        title={title}
        icon={ListTodo}
        actions={
          <Link
            to="/jobs"
            className="text-fg-3 hover:text-foreground inline-flex items-center gap-1 text-xs transition-colors duration-100"
          >
            <ArrowLeft className="size-3.5" aria-hidden />
            All jobs
          </Link>
        }
      />
      <Page>
        <Surface>{children}</Surface>
      </Page>
    </>
  );
}

/** The way back to the job this task came out of, in the header. */
function BackToJob({ briefId }: { briefId: string }) {
  return (
    <Link
      to="/jobs/$briefId"
      params={{ briefId }}
      className="text-fg-3 hover:text-foreground inline-flex items-center gap-1 text-xs transition-colors duration-100"
    >
      <ArrowLeft className="size-3.5" aria-hidden />
      The job
    </Link>
  );
}

/**
 * What the task is, above the fold at every width.
 *
 * The header's own clause is hidden under `sm` (`ViewHeader`), and the key, the
 * state and the job are the three things the acceptance criteria say a person
 * must not have to scroll for - so they are a line of the page rather than a
 * line of the header, and the header carries only the age.
 */
function Identity({ task, shape }: { task: Task; shape: TaskShape }) {
  return (
    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 px-0.5 text-xs">
      <TaskKey briefId={task.briefId}>{task.key}</TaskKey>
      {/* State, size and repo are chips drawn from the shared state vocabulary
          now (issue #64), so a task's shape reads as one row of chips rather
          than a word and two labels. The icon lives on the chip itself, so
          there is no separate bare `TaskIcon` beside the key any more. */}
      <TaskStateChip status={task.status} />
      {shape.isGate ? <span className="text-fg-3">{duration(shape.waited)} waiting</span> : null}
      <RepoChip repo={task.repo} />
      <SizeChip size={task.size} />
      {task.pausedAt ? <PausedChip /> : null}
      <span className="text-fg-3 min-w-0 truncate">
        in <BriefLink briefId={task.briefId} title={shape.brief?.title} className="text-fg-2" />
      </span>
    </div>
  );
}

export function TaskPage() {
  const { taskId } = useParams({ strict: false }) as { taskId: string };
  const detail = useQuery({
    queryKey: hubKeys.task(taskId),
    queryFn: () => api.task(taskId),
    // A page of live state takes the same 30s fallback every other live surface
    // has behind the event stream.
    refetchInterval: 30_000,
  });

  if (detail.isPending) {
    return (
      <Shell title="Task">
        <Empty>Loading…</Empty>
      </Shell>
    );
  }
  if (detail.isError) {
    return (
      <Shell title="Task">
        <Empty>{detail.error instanceof Error ? detail.error.message : 'no such task'}</Empty>
      </Shell>
    );
  }
  return <TaskView detail={detail.data} />;
}

/**
 * The page itself, once there is a task to draw. Split out because
 * `useTaskShape` is a hook and the two cases above return before there is a
 * task to hand it.
 */
function TaskView({ detail }: { detail: TaskRecord }) {
  const { task, events, notes, attempts } = detail;
  const shape = useTaskShape(task, events);
  const decision = taskDecision(task, shape);
  // The bare `cancel` kind has no banner - it is the admin's only verb on
  // something in flight, "Cancel task" and nothing explaining it - so it moves
  // into the header popover with Pause and Change rather than staying pinned
  // as its own card (issue #89). `gate`/`failed`/`stuck`/`waiting` keep the
  // pinned card: each already explains the state it is a decision about.
  const pinnedDecision = decision && decision !== 'cancel' ? decision : null;
  // What the run region would have in it. Each of the three parts draws
  // nothing when it has nothing, so without this the page would open a heading
  // and an empty surface over a task that has not started - and the two that
  // can come first decide where the hairlines go, since a border-top under
  // nothing is a line floating at the top of a card.
  const hasSteps = taskProgressSteps(task) !== null;
  const showsSummary = decision !== 'gate' && Boolean(task.runSummary?.trim());
  const hasRun = hasSteps || showsSummary || attempts.length > 0;

  return (
    <>
      <ViewHeader
        title={task.title}
        icon={ListTodo}
        sub={`${duration(minutesSince(task.updatedAt))} since the last change`}
        actions={
          <>
            <TaskVerbsPopover task={task} shape={shape} />
            <BackToJob briefId={task.briefId} />
          </>
        }
      />
      <Page>
        <div className="dy-stack">
          <Identity task={task} shape={shape} />

          {pinnedDecision ? (
            // Pinned under the header, which is 4rem tall, for the same reason
            // the job page pins its gate: it is why most people opened this.
            // Only from `lg`, a height decision rather than a width one - on a
            // phone the card is four lines and a 36px button row, and pinning
            // it would spend a quarter of the screen on what the page opens
            // with anyway.
            <div className="lg:sticky lg:top-16 lg:z-10">
              <Surface>
                <TaskDecision task={task} shape={shape} />
              </Surface>
            </div>
          ) : null}

          {/* Every fact and every instant, as one band across the page.

              These rows used to be the right-hand half of an equal split: the
              spec, often two thousand words of it, in one column, and five
              label/value rows plus a Timings block in the other. This
              overrules that decision deliberately. Equal halves are only right
              when the two things are the same size, and these two never are:
              properties are glanced at rather than read, so they want width
              and almost no height, which is a band across the top; prose is
              read, so it wants a measure and all the height it needs. The old
              split gave the short thing half the page and the long thing half
              the width, and the reader paid for it in a screen of dead space
              beside a column of text. */}
          <Surface>
            <TaskProperties task={task} shape={shape} />
          </Surface>

          {/* The spec at a reading width, and everything that happened to it in
              a rail beside it. Only from `xl`: the rail holds the notes thread
              and the journal, both of which are conversations, and a
              conversation squeezed into a third of a laptop screen is worse
              than one under the spec. Below `xl` the grid is one column and the
              page reads spec, run, notes, journal, top to bottom. */}
          <div className="grid gap-[var(--dy-gap)] xl:grid-cols-[minmax(0,1fr)_minmax(0,24rem)]">
            <div className="dy-stack min-w-0">
              <Section>
                <SectionHead
                  title="The spec"
                  meta={
                    task.acceptanceCriteria.length
                      ? `${task.acceptanceCriteria.length} criteri${
                          task.acceptanceCriteria.length === 1 ? 'on' : 'a'
                        }`
                      : undefined
                  }
                />
                <Surface>
                  <TaskSpec task={task} />
                </Surface>
              </Section>

              {/* What came of the spec: the steps the agent is on, its own
                  account of the run, and every attempt behind it. Drawn only
                  when one of the three has something to say, because a heading
                  over an empty card is a promise the page does not keep - a
                  queued task has no run yet, and says so by not having the
                  region at all.

                  No pull request link on the heading: the properties above
                  carry one, and at the gate the decision card carries it next
                  to the buttons it belongs to. Three copies of one link on one
                  page is not three chances to find it. */}
              {hasRun ? (
                <Section>
                  <SectionHead title="The run" />
                  <Surface>
                    <TaskProgressList task={task} bordered={false} />
                    {/* The agent's own account of the run. At a gate the
                        decision card above already carries it, next to the
                        buttons it is evidence for, so it is not drawn twice. */}
                    {showsSummary ? (
                      <div className={cn('pb-2.5', hasSteps && 'border-t')}>
                        <RunSummary summary={task.runSummary} taskId={task.id} />
                      </div>
                    ) : null}
                    <TaskAttempts attempts={attempts} bordered={hasSteps || showsSummary} />
                  </Surface>
                </Section>
              ) : null}
            </div>

            <div className="dy-stack min-w-0">
              <Section>
                <SectionHead
                  title="Notes and findings"
                  meta={notes.length ? String(notes.length) : undefined}
                />
                <Surface>
                  <TaskNotes task={task} notes={notes} heading={false} />
                </Surface>
              </Section>

              <EventLogSection
                events={events}
                empty="Nothing has happened on this task yet."
                scope="task.events"
              />
            </div>
          </div>
        </div>
      </Page>
    </>
  );
}
